import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  Svg,
  Path,
  Line,
} from "@react-pdf/renderer";

const DEFAULT_PRIMARY = "#0C3E71";
const GRAY_100 = "#f3f4f6";
const GRAY_200 = "#e5e7eb";
const GRAY_300 = "#d1d5db";
const GRAY_400 = "#9ca3af";
const GRAY_500 = "#6b7280";
const GRAY_600 = "#4b5563";
const GRAY_700 = "#374151";
const GRAY_900 = "#111827";
const WHITE = "#ffffff";

const s = StyleSheet.create({
  page: { padding: 16, fontFamily: "Helvetica", fontSize: 9, color: GRAY_900 },
  // Header
  header: { backgroundColor: WHITE, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  logoRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  logoText: { fontSize: 24, fontFamily: "Helvetica-Bold" },
  operatorBlock: { alignItems: "flex-end", gap: 2 },
  operatorName: { fontSize: 10, fontFamily: "Helvetica-Bold" },
  operatorDetail: { fontSize: 8, color: GRAY_600 },
  // Client bar
  clientBar: { backgroundColor: DEFAULT_PRIMARY, borderRadius: 8, paddingHorizontal: 12, height: 24, flexDirection: "row", alignItems: "center", gap: 8, marginTop: 16 },
  clientField: { color: WHITE, fontSize: 8 },
  // Main blue container
  blueBox: { backgroundColor: DEFAULT_PRIMARY, borderRadius: 8, padding: 12, marginTop: 16, gap: 12 },
  // Aircraft + Leg summary row
  summaryRow: { flexDirection: "row", gap: 12 },
  aircraftPanel: { backgroundColor: WHITE, borderRadius: 8, overflow: "hidden", flex: 1 },
  aircraftImage: { width: "100%", height: 110, objectFit: "cover" },
  aircraftImagePlaceholder: { width: "100%", height: 110, backgroundColor: GRAY_100 },
  aircraftInfo: { padding: 12 },
  aircraftTail: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  aircraftMeta: { flexDirection: "row", gap: 8, marginTop: 4, fontSize: 7, color: GRAY_500 },
  // Aircraft Note pill — amber callout matches on-screen trip sheet.
  aircraftNoteBox: {
    marginTop: 8, padding: 6,
    backgroundColor: "#FFFBEB",             // bg-amber-50
    borderWidth: 1, borderColor: "#FDE68A", // border-amber-100/200 blend for print
    borderRadius: 4,
  },
  aircraftNoteLabel: {
    fontSize: 6, fontFamily: "Helvetica-Bold",
    color: "#78350F",                        // text-amber-900
    textTransform: "uppercase", letterSpacing: 0.4,
    marginBottom: 2,
  },
  aircraftNoteBody: {
    fontSize: 8, color: "#92400E",           // text-amber-800
    lineHeight: 1.3,
  },
  legSummaryPanel: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 2 },
  legSummaryTitle: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  legSummaryRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 6, fontSize: 7, color: GRAY_600 },
  legSummaryLegNum: { fontFamily: "Helvetica-Bold", color: GRAY_900, fontSize: 8 },
  legSummaryCode: { fontFamily: "Helvetica-Bold", fontSize: 7 },
  codePill: { flexDirection: "row", alignItems: "center", gap: 3, backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 },
  summaryField: { flexDirection: "row", alignItems: "center", gap: 3 },
  // Leg detail
  legLabel: { color: WHITE, fontSize: 9, fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  sectionLabel: { color: "rgba(255,255,255,0.7)", fontSize: 7, fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  dashedSep: { marginVertical: 12, height: 2, width: "100%" },
  legDetailRow: { flexDirection: "row", gap: 12 },
  legLeftCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 2 },
  legSideCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 1 },
  codeRow: { flexDirection: "row", alignItems: "center", gap: 6, marginTop: 6 },
  codeText: { fontSize: 22, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  codeLine: { flex: 1, height: 0.5, backgroundColor: GRAY_300 },
  timeGrid: { flexDirection: "row", justifyContent: "space-between", marginTop: 10 },
  timeCol: { gap: 1 },
  timeLabel: { fontSize: 6, color: GRAY_400, textTransform: "uppercase" },
  timeValue: { fontSize: 7, color: GRAY_500 },
  timeValueBold: { fontSize: 9, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  durationText: { fontSize: 7, color: GRAY_500, textAlign: "center" },
  // Airport fields
  airportLabel: { fontSize: 6, color: GRAY_400, textTransform: "uppercase", marginBottom: 2 },
  airportName: { fontSize: 9, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  airportFbo: { fontSize: 7, fontFamily: "Helvetica-Bold", color: GRAY_900, marginTop: 2 },
  airportAddress: { fontSize: 7, color: GRAY_500, marginTop: 2 },
  // Crew & Passengers (& Note)
  //   · notes present  →  crew col + passengers col (2x) + note col   (1/2/1)
  //   · notes empty    →  crew col + passengers col                    (1/1)
  // Flex ratios set inline per-column in the row render so the same
  // stylesheet drives both variants.
  crewPassRow: { flexDirection: "row", alignItems: "stretch", gap: 12, marginTop: 12 },
  crewCol: { flex: 1, flexDirection: "column" },
  passCol: { flex: 1, flexDirection: "column" },
  noteCol: { flex: 1, flexDirection: "column" },
  whiteCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12 },
  crewGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  crewItem: { width: "48%", backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  // Full-width variant — used when the Crew column shrinks to 25% (notes
  // + passengers both present) so long names don't get squeezed by the
  // 48% chip width the wider layout uses.
  crewItemFull: { width: "100%", backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  crewName: { fontSize: 8, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  crewPhone: { fontSize: 7, color: GRAY_500 },
  passGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  passItem: { width: "48%", backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  passName: { fontSize: 8, color: GRAY_900 },
  noteItem: { backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  noteText: { fontSize: 8, color: GRAY_900, lineHeight: 1.4 },
  // Services table
  serviceTable: { backgroundColor: WHITE, borderRadius: 8, overflow: "hidden" },
  serviceHeaderRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: GRAY_200, paddingVertical: 4, paddingHorizontal: 8 },
  serviceHeaderCell: { fontSize: 6, color: GRAY_500, textTransform: "uppercase", fontFamily: "Helvetica-Bold" },
  serviceRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: GRAY_100, paddingVertical: 6, paddingHorizontal: 8, alignItems: "center" },
  serviceCell: { fontSize: 8, color: GRAY_600 },
  serviceCellBold: { fontSize: 8, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  // Aircraft media row (trailing, matches mobile CSS in tripSheetPdfGeneratorOld.ts)
  mediaSection: { marginTop: 16 },
  mediaTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.45,
    letterSpacing: 0.64,
    textTransform: "uppercase",
    marginBottom: 11,
    color: "rgba(255,255,255,0.7)",
  },
  mediaRow: { flexDirection: "row", gap: 11 },
  mediaCard: {
    // Fixed 1/3 width (not flex:1) so a 2-card row keeps the same card size
    // as the 3-card row instead of stretching each half. The remaining slot
    // stays empty rather than redistributing.
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "32%",
    height: 120,
    borderRadius: 11,
    overflow: "hidden",
    // White so any pixel-level gap in the JPEG capture (rare, from
    // downsampling) still reads clean instead of tinting the map card.
    backgroundColor: "#FFFFFF",
  },
  mediaCardImg: { width: "100%", height: "100%", objectFit: "cover" },
  // Footer
  footer: { textAlign: "center", fontSize: 7, color: "rgba(255,255,255,0.4)", marginTop: 4 },
  // placeholders
  placeholder: { color: GRAY_400 },
});

const val = (v) => v || "-";

// SVG Icon components using @react-pdf Svg/Path primitives
const Icon = ({ d, size = 10, color = GRAY_900, rotate }) => (
  <Svg viewBox="0 0 24 24" width={size} height={size} style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined}>
    <Path d={d} fill={color} fillRule="evenodd" />
  </Svg>
);

const ICON_PATHS = {
  plane: "M14 8.94737L22 14V16L14 13.4737V18.8333L17 20.5V22L12.5 21L8 22V20.5L11 18.8333V13.4737L3 16V14L11 8.94737V3.5C11 2.67157 11.6716 2 12.5 2C13.3284 2 14 2.67157 14 3.5V8.94737Z",
  mapPin: "M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z",
  phone: "M9.36556 10.6821C10.302 12.3288 11.6712 13.698 13.3179 14.6344L14.2024 13.3961C14.4965 12.9845 15.0516 12.8573 15.4956 13.0998C16.9024 13.8683 18.4571 14.3353 20.0789 14.4637C20.599 14.5049 21 14.9389 21 15.4606V19.9234C21 20.4361 20.6122 20.8657 20.1022 20.9181C19.5723 20.9726 19.0377 21 18.5 21C9.93959 21 3 14.0604 3 5.5C3 4.96227 3.02742 4.42771 3.08189 3.89776C3.1343 3.38775 3.56394 3 4.07665 3H8.53942C9.0611 3 9.49513 3.40104 9.5363 3.92109C9.66467 5.54288 10.1317 7.09764 10.9002 8.50444C11.1427 8.9484 11.0155 9.50354 10.6039 9.79757L9.36556 10.6821ZM6.84425 10.0252L8.7442 8.66809C8.20547 7.50514 7.83628 6.27183 7.64727 5H5.00907C5.00303 5.16632 5 5.333 5 5.5C5 12.9558 11.0442 19 18.5 19C18.667 19 18.8337 18.997 19 18.9909V16.3527C17.7282 16.1637 16.4949 15.7945 15.3319 15.2558L13.9748 17.1558C13.4258 16.9425 12.8956 16.6915 12.3874 16.4061L12.3293 16.373C10.3697 15.2587 8.74134 13.6303 7.627 11.6707L7.59394 11.6126C7.30849 11.1044 7.05754 10.5742 6.84425 10.0252Z",
  sun: "M12 18C8.68629 18 6 15.3137 6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 15.3137 15.3137 18 12 18ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16ZM11 1H13V4H11V1ZM11 20H13V23H11V20ZM3.51472 4.92893L4.92893 3.51472L7.05025 5.63604L5.63604 7.05025L3.51472 4.92893ZM16.9497 18.364L18.364 16.9497L20.4853 19.0711L19.0711 20.4853L16.9497 18.364ZM19.0711 3.51472L20.4853 4.92893L18.364 7.05025L16.9497 5.63604L19.0711 3.51472ZM5.63604 16.9497L7.05025 18.364L4.92893 20.4853L3.51472 19.0711L5.63604 16.9497ZM23 11V13H20V11H23ZM4 11V13H1V11H4Z",
  takeoff: "M21.949 10.1118C22.1634 10.912 21.6886 11.7345 20.8884 11.9489L5.2218 16.1467C4.77856 16.2655 4.31138 16.0674 4.08866 15.6662L1.46582 10.9415L2.91471 10.5533L5.3825 12.9979L10.4778 11.6326L5.96728 4.55896L7.89913 4.04132L14.8505 10.4609L20.1119 9.05113C20.9121 8.83671 21.7346 9.31159 21.949 10.1118ZM4.00013 19H20.0001V21H4.00013V19Z",
  landing: "M20.949 14.8882C20.7346 15.6884 19.9121 16.1633 19.1119 15.9489L3.44528 11.751C3.00205 11.6323 2.69653 11.2271 2.70424 10.7683L2.79514 5.36515L4.24403 5.75338L5.15891 9.10435L10.2542 10.4696L9.88479 2.08844L11.8166 2.60608L14.6269 11.6413L19.8883 13.0511C20.6885 13.2655 21.1634 14.088 20.949 14.8882ZM4.00008 19H20.0001V21H4.00008V19Z",
  arrowRight: "M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z",
  time: "M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17V14H11V7H13V12Z",
  calendar: "M17 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9V3H15V1H17V3ZM20 11H4V19H20V11ZM15 5H9V7H7V5H4V9H20V5H17V7H15V5Z",
  user: "M4 22C4 17.5817 7.58172 14 12 14C16.4183 14 20 17.5817 20 22H18C18 18.6863 15.3137 16 12 16C8.68629 16 6 18.6863 6 22H4ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11Z",
  // Service-category icons — path data extracted from the custom SVGs in
  // src/assets/images/service-icons. Same 24x24 viewBox as the rest.
  svcTransportation: "M18.3 18.4H5.7V19.3C5.7 19.5387 5.60518 19.7676 5.4364 19.9364C5.26761 20.1052 5.03869 20.2 4.8 20.2H3.9C3.66131 20.2 3.43239 20.1052 3.2636 19.9364C3.09482 19.7676 3 19.5387 3 19.3V10.3L5.232 5.0908C5.37089 4.7668 5.60186 4.49071 5.89625 4.2968C6.19064 4.10289 6.53549 3.99969 6.888 4H17.112C17.4642 4.00004 17.8087 4.10341 18.1027 4.2973C18.3967 4.49119 18.6274 4.76708 18.7662 5.0908L21 10.3V19.3C21 19.5387 20.9052 19.7676 20.7364 19.9364C20.5676 20.1052 20.3387 20.2 20.1 20.2H19.2C18.9613 20.2 18.7324 20.1052 18.5636 19.9364C18.3948 19.7676 18.3 19.5387 18.3 19.3V18.4ZM19.2 12.1H4.8V16.6H19.2V12.1ZM4.9584 10.3H19.0416L17.1129 5.8H6.888L4.9593 10.3H4.9584ZM7.05 15.7C6.69196 15.7 6.34858 15.5578 6.09541 15.3046C5.84223 15.0514 5.7 14.708 5.7 14.35C5.7 13.992 5.84223 13.6486 6.09541 13.3954C6.34858 13.1422 6.69196 13 7.05 13C7.40804 13 7.75142 13.1422 8.00459 13.3954C8.25777 13.6486 8.4 13.992 8.4 14.35C8.4 14.708 8.25777 15.0514 8.00459 15.3046C7.75142 15.5578 7.40804 15.7 7.05 15.7ZM16.95 15.7C16.592 15.7 16.2486 15.5578 15.9954 15.3046C15.7422 15.0514 15.6 14.708 15.6 14.35C15.6 13.992 15.7422 13.6486 15.9954 13.3954C16.2486 13.1422 16.592 13 16.95 13C17.308 13 17.6514 13.1422 17.9046 13.3954C18.1578 13.6486 18.3 13.992 18.3 14.35C18.3 14.708 18.1578 15.0514 17.9046 15.3046C17.6514 15.5578 17.308 15.7 16.95 15.7Z",
  svcCatering: "M14.0412 12.1314L13.2726 12.9L19.6365 19.2639L18.3639 20.5365L12 14.1735L5.63606 20.5365L4.36346 19.2639L12.7686 10.8588C12.2394 9.54748 12.7866 7.65928 14.2266 6.21748C15.9843 4.46068 18.4062 4.03318 19.6365 5.26348C20.8668 6.49378 20.4393 8.91568 18.6825 10.6725C17.2407 12.1143 15.3525 12.6615 14.0412 12.1314ZM4.99976 4.62718L11.0451 10.6725L8.49986 13.2186L4.99886 9.71758C4.32396 9.04248 3.94482 8.12697 3.94482 7.17238C3.94482 6.21779 4.32396 5.30228 4.99886 4.62718H4.99976ZM17.409 9.39898C18.5412 8.26768 18.7743 6.94558 18.3639 6.53518C17.9535 6.12478 16.6314 6.35788 15.5001 7.48918C14.3688 8.62138 14.1357 9.94348 14.5461 10.3539C14.9556 10.7634 16.2777 10.5303 17.409 9.39898Z",
  svcBeverage: "M11.1 18.3V13.7001L3.90002 5.70002V3.90002H20.1V5.70002L12.9 13.7001V18.3H17.4V20.1H6.60002V18.3H11.1ZM7.94102 7.50002H16.059L17.679 5.70002H6.32102L7.94102 7.50002ZM9.56102 9.30002L12 12.009L14.439 9.30002H9.56102Z",
  svcPassengerServices: "M16.5 4.80005C17.9321 4.80005 19.3057 5.36898 20.3184 6.38167C21.331 7.39437 21.9 8.76788 21.9 10.2V13.8C21.9 15.2322 21.331 16.6057 20.3184 17.6184C19.3057 18.6311 17.9321 19.2 16.5 19.2H7.49998C6.06781 19.2 4.6943 18.6311 3.6816 17.6184C2.6689 16.6057 2.09998 15.2322 2.09998 13.8V10.2C2.09998 8.76788 2.6689 7.39437 3.6816 6.38167C4.6943 5.36898 6.06781 4.80005 7.49998 4.80005H16.5ZM16.5 6.60005H7.49998C6.57636 6.60005 5.68807 6.95504 5.01885 7.5916C4.34962 8.22816 3.95066 9.09759 3.90448 10.02L3.89998 10.2V13.8C3.89997 14.7237 4.25497 15.612 4.89153 16.2812C5.52809 16.9504 6.39751 17.3494 7.31998 17.3955L7.49998 17.4H16.5C17.4236 17.4 18.3119 17.0451 18.9811 16.4085C19.6503 15.7719 20.0493 14.9025 20.0955 13.98L20.1 13.8V10.2C20.1 9.27643 19.745 8.38814 19.1084 7.71892C18.4719 7.04969 17.6024 6.65073 16.68 6.60455L16.5 6.60005ZM10.2 9.30005V11.1H12V12.9H10.1991L10.2 14.7H8.39998L8.39908 12.9H6.59998V11.1H8.39998V9.30005H10.2ZM17.4 12.9V14.7H15.6V12.9H17.4ZM15.6 9.30005V11.1H13.8V9.30005H15.6Z",
  svcWellness: "M15.6 2.09998C16.0968 2.09998 16.5 2.50318 16.5 2.99998V5.69998H20.1C20.5968 5.69998 21 6.10318 21 6.59998V19.2C21 19.6968 20.5968 20.1 20.1 20.1H3.9C3.4032 20.1 3 19.6968 3 19.2V6.59998C3 6.10318 3.4032 5.69998 3.9 5.69998H7.5V2.99998C7.5 2.50318 7.9032 2.09998 8.4 2.09998H15.6ZM19.2 7.49998H4.8V18.3H19.2V7.49998ZM12.9 9.29998V12H15.6V13.8H12.8991L12.9 16.5H11.1L11.0991 13.8H8.4V12H11.1V9.29998H12.9ZM14.7 3.89998H9.3V5.69998H14.7V3.89998Z",
  svcSecurity: "M6.60002 8.39998V7.49998C6.60002 6.06781 7.16895 4.6943 8.18165 3.6816C9.19434 2.6689 10.5679 2.09998 12 2.09998C13.4322 2.09998 14.8057 2.6689 15.8184 3.6816C16.8311 4.6943 17.4 6.06781 17.4 7.49998V8.39998H19.2C19.4387 8.39998 19.6676 8.4948 19.8364 8.66358C20.0052 8.83236 20.1 9.06128 20.1 9.29998V20.1C20.1 20.3387 20.0052 20.5676 19.8364 20.7364C19.6676 20.9052 19.4387 21 19.2 21H4.80002C4.56133 21 4.33241 20.9052 4.16363 20.7364C3.99485 20.5676 3.90002 20.3387 3.90002 20.1V9.29998C3.90002 9.06128 3.99485 8.83236 4.16363 8.66358C4.33241 8.4948 4.56133 8.39998 4.80002 8.39998H6.60002ZM18.3 10.2H5.70002V19.2H18.3V10.2ZM11.1 15.3588C10.7569 15.1607 10.4887 14.8548 10.337 14.4888C10.1854 14.1227 10.1588 13.7168 10.2614 13.3341C10.3639 12.9513 10.5899 12.6131 10.9043 12.3719C11.2186 12.1307 11.6038 11.9999 12 11.9999C12.3963 11.9999 12.7814 12.1307 13.0958 12.3719C13.4102 12.6131 13.6361 12.9513 13.7387 13.3341C13.8412 13.7168 13.8146 14.1227 13.663 14.4888C13.5114 14.8548 13.2432 15.1607 12.9 15.3588V17.4H11.1V15.3588ZM8.40002 8.39998H15.6V7.49998C15.6 6.5452 15.2207 5.62952 14.5456 4.95439C13.8705 4.27926 12.9548 3.89998 12 3.89998C11.0452 3.89998 10.1296 4.27926 9.45444 4.95439C8.77931 5.62952 8.40002 6.5452 8.40002 7.49998V8.39998Z",
  svcConcierge: "M21 20.1H3V18.3H3.9V4.80002C3.9 4.56133 3.99482 4.33241 4.1636 4.16363C4.33239 3.99485 4.56131 3.90002 4.8 3.90002H17.4C17.6387 3.90002 17.8676 3.99485 18.0364 4.16363C18.2052 4.33241 18.3 4.56133 18.3 4.80002V9.30002H20.1V18.3H21V20.1ZM16.5 18.3H18.3V11.1H12.9V18.3H14.7V12.9H16.5V18.3ZM16.5 9.30002V5.70002H5.7V18.3H11.1V9.30002H16.5ZM7.5 11.1H9.3V12.9H7.5V11.1ZM7.5 14.7H9.3V16.5H7.5V14.7ZM7.5 7.50002H9.3V9.30002H7.5V7.50002Z",
  svcTripProtection: "M5.70002 5.34358V13.6101C5.70003 14.2027 5.84634 14.7861 6.12596 15.3086C6.40558 15.8311 6.80986 16.2765 7.30292 16.6053L12 19.7373L16.6971 16.6053C17.1901 16.2766 17.5943 15.8314 17.8739 15.309C18.1535 14.7867 18.2999 14.2034 18.3 13.611V5.34358L12 3.94498L5.70002 5.34358ZM4.60472 3.74338L12 2.09998L19.3953 3.74338C19.5952 3.7878 19.7739 3.89904 19.902 4.05874C20.0302 4.21844 20.1 4.41704 20.1 4.62178V13.6101C20.1 14.499 19.8804 15.3742 19.4609 16.158C19.0414 16.9417 18.4349 17.6098 17.6952 18.1029L12 21.9L6.30482 18.1029C5.56527 17.6099 4.95884 16.942 4.53934 16.1584C4.11984 15.3748 3.90024 14.4998 3.90002 13.611V4.62178C3.90006 4.41704 3.9699 4.21844 4.09802 4.05874C4.22613 3.89904 4.40487 3.7878 4.60472 3.74338ZM12 13.35L9.35492 14.7405L9.85982 11.7957L7.72052 9.70948L10.6779 9.27928L12 6.59998L13.323 9.27928L16.2795 9.70948L14.1402 11.7957L14.6442 14.7405L12 13.35Z",
};

// Map service category name → the ICON_PATHS key that draws its icon.
// Matches SERVICE_CATEGORIES in AddServiceModal exactly.
const SERVICE_ICON_KEYS = {
  "Transportation": "svcTransportation",
  "Catering": "svcCatering",
  "Beverage & Bar": "svcBeverage",
  "Passenger Services": "svcPassengerServices",
  "Wellness & Medical": "svcWellness",
  "Security": "svcSecurity",
  "Concierge & Documents": "svcConcierge",
  "Trip Protection": "svcTripProtection",
};

const AirportSection = ({ title, data }) => (
  <View>
    <Text style={s.airportLabel}>{title}</Text>
    <Text style={s.airportName}>{val(data.airportName)}</Text>
    {!!data.fbo && <Text style={s.airportFbo}>{data.fbo}</Text>}
    <Text style={s.airportAddress}>{val(data.address)}</Text>
  </View>
);

const TripSheetPdf = ({ data, brandColor }) => {
  const PRIMARY = brandColor || DEFAULT_PRIMARY;

  // Build dynamic overrides for styles that use PRIMARY
  const dynamicStyles = {
    clientBar: { ...s.clientBar, backgroundColor: PRIMARY },
    blueBox: { ...s.blueBox, backgroundColor: PRIMARY },
  };

  return (
  <Document>
    <Page size="A4" style={s.page}>
      {/* HEADER */}
      <View style={s.header}>
        <View style={s.logoRow}>
          {data.operatorLogo ? (
             <View style={{ backgroundColor: PRIMARY, borderRadius: 6, padding: 6, alignItems: "center", justifyContent: "center" }}>
              <Image
                src={data.operatorLogo}
                style={{ width: 100, height: 60, objectFit: "contain" }}
              />
            </View>
          ) : (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Image src="/images/icons/planeright.svg" style={{ width: 20, height: 20 }} />
              <Text style={s.logoText}>Logo</Text>
            </View>
          )}
        </View>
        <View style={s.operatorBlock}>
          <Text style={s.operatorName}>{val(data.operator.name)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.address)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.phone)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.fax)}</Text>
        </View>
      </View>

      {/* CLIENT BAR */}
      <View style={dynamicStyles.clientBar}>
        <Text style={[s.clientField, { flex: 1, fontFamily: "Helvetica-Bold" }]}>{val(data.client.name)}</Text>
        <Text style={[s.clientField, { flex: 1 }]}>{val(data.client.company)}</Text>
        <Text style={[s.clientField, { flex: 1.3 }]}>{val(data.client.email)}</Text>
        <Text style={[s.clientField, { flex: 1, textAlign: "right" }]}>{val(data.client.phone)}</Text>
      </View>

      {/* MAIN BLUE CONTAINER */}
      <View style={dynamicStyles.blueBox}>
        {/* Aircraft + Leg Summary */}
        <View style={s.summaryRow}>
          <View style={s.aircraftPanel}>
            {data.aircraft.image ? (
              <Image
                src={
                  typeof data.aircraft.image === "string" && data.aircraft.image.startsWith("data:")
                    ? data.aircraft.image
                    : { uri: Array.isArray(data.aircraft.image) ? data.aircraft.image[0] : data.aircraft.image, method: "GET" }
                }
                style={s.aircraftImage}
              />
            ) : (
              <View style={s.aircraftImagePlaceholder} />
            )}
            <View style={s.aircraftInfo}>
              <Text style={s.aircraftTail}>{val(data.aircraft.tailNumber)}</Text>
              <View style={s.aircraftMeta}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                  <Icon d={ICON_PATHS.takeoff} size={8} color={GRAY_500} />
                  <Text>{val(data.aircraft.name)}</Text>
                </View>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                  <Icon d={ICON_PATHS.user} size={7} color={GRAY_500} />
                  <Text>{val(String(data.aircraft.passengerCount))}</Text>
                </View>
              </View>

              {/* Aircraft Note — amber callout matches the on-screen
                  trip sheet + itinerary email. Rendered only when set. */}
              {data.aircraft.note ? (
                <View style={s.aircraftNoteBox}>
                  <Text style={s.aircraftNoteLabel}>Aircraft Note</Text>
                  <Text style={s.aircraftNoteBody}>{data.aircraft.note}</Text>
                </View>
              ) : null}
            </View>
          </View>

          <View style={s.legSummaryPanel}>
            <Text style={s.legSummaryTitle}>Leg Summary</Text>
            {data.legs.map((leg, idx) => (
              <View key={idx} style={s.legSummaryRow}>
                <Text style={s.legSummaryLegNum}>LEG {leg.legNumber}</Text>
                <View style={s.codePill}>
                  <Icon d={ICON_PATHS.takeoff} size={7} color={PRIMARY} />
                  <Text style={s.legSummaryCode}>{val(leg.departure.code)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.calendar} size={7} color={GRAY_400} />
                  <Text>{val(leg.departure.date)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.time} size={7} color={GRAY_400} />
                  <Text>{val(leg.departure.time)}</Text>
                </View>
                <Icon d={ICON_PATHS.arrowRight} size={8} color={GRAY_700} />
                <View style={s.codePill}>
                  <Icon d={ICON_PATHS.landing} size={7} color={PRIMARY} />
                  <Text style={s.legSummaryCode}>{val(leg.arrival.code)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.calendar} size={7} color={GRAY_400} />
                  <Text>{val(leg.arrival.date)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.time} size={7} color={GRAY_400} />
                  <Text>{val(leg.arrival.time)}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* LEG DETAILS */}
        {data.legs.map((leg, idx) => (
          <View key={idx} wrap={false}>
            {idx > 0 && (
              <Svg style={s.dashedSep} viewBox="0 0 1000 2" preserveAspectRatio="none">
                <Line
                  x1="0"
                  y1="1"
                  x2="1000"
                  y2="1"
                  stroke="#ffffff"
                  strokeOpacity="0.3"
                  strokeWidth="2"
                  strokeDasharray="8 6"
                />
              </Svg>
            )}
            <Text style={s.legLabel}>Leg {leg.legNumber}</Text>

            {/* Leg detail row: 3 separate cards */}
            <View style={s.legDetailRow}>
              {/* Left: Codes & Times */}
              <View style={s.legLeftCard}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 6, color: GRAY_400, textTransform: "uppercase" }}>{val(leg.timezone)}</Text>
                  <Text style={{ fontSize: 6, color: GRAY_400, textTransform: "uppercase" }}>{val(leg.flightDistance)}</Text>
                </View>
                <View style={s.codeRow}>
                  <Text style={s.codeText}>{val(leg.departure.code)}</Text>
                  <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 4 }}>
                    <View style={s.codeLine} />
                    <Icon d={ICON_PATHS.plane} size={14} color={PRIMARY} rotate={90} />
                    <View style={s.codeLine} />
                  </View>
                  <Text style={s.codeText}>{val(leg.arrival.code)}</Text>
                </View>
                <View style={s.timeGrid}>
                  <View style={s.timeCol}>
                    <Text style={s.timeValueBold}>{val(leg.departure.time)}</Text>
                    <Text style={s.timeValue}>{val(leg.departure.date)}</Text>
                    <Text style={s.timeValue}>{val(leg.departure.day)}</Text>
                  </View>
                  <View style={[s.timeCol, { alignItems: "center" }]}>
                    <Text style={s.durationText}>{val(leg.flightDuration)}</Text>
                  </View>
                  <View style={[s.timeCol, { alignItems: "flex-end" }]}>
                    <Text style={s.timeValueBold}>{val(leg.arrival.time)}</Text>
                    <Text style={s.timeValue}>{val(leg.arrival.date)}</Text>
                    <Text style={s.timeValue}>{val(leg.arrival.day)}</Text>
                  </View>
                </View>
              </View>

              {/* Center: Departure Airport */}
              <View style={s.legSideCard}>
                <AirportSection title="Departure" data={leg.departure} />
              </View>

              {/* Right: Arrival Airport */}
              <View style={s.legSideCard}>
                <AirportSection title="Arrival" data={leg.arrival} />
              </View>
            </View>

            {/* Crew & Passengers (& Note) — mirrors the preview drawer's
                col-span logic via inline flex ratios. Note stays pinned
                at flex:1 whenever anything else is in the row, so it
                never grows past 25% of the row width.
                  · crew + pass + note → flex 1 | 2 | 1  → 25/50/25
                  · pass + note only    → flex 3 | 1     → 75/25
                  · crew + note only    → flex 3 | 1     → 75/25
                  · note only           → flex 1 (full row)
                  · crew + pass no note → 1 | 1 (original)
                  · only crew or pass   → renders in LEFT col, spacer RIGHT
                Crew chip width shrinks to 100% when the crew column is
                narrow (notes + passengers present) so long names don't
                get squeezed by the 48% chip layout. */}
            {(leg.crew.length > 0 || leg.passengers.length > 0 || leg.notes) && (
              <View style={s.crewPassRow}>
                {/* Crew column — flex-3 when notes are present but no
                    passengers, so crew stretches to fill the leftover. */}
                {leg.crew.length > 0 && (
                  <View style={[
                    s.crewCol,
                    leg.notes && leg.passengers.length === 0 ? { flex: 3 } : null,
                  ]}>
                    <Text style={s.sectionLabel}>Crew</Text>
                    <View style={[s.whiteCard, { flexGrow: 1 }]}>
                      <View style={s.crewGrid}>
                        {leg.crew.map((member, i) => {
                          // Narrow crew col (25%): one chip per row.
                          const useFullWidth = leg.notes && leg.passengers.length > 0;
                          return (
                            <View key={i} style={useFullWidth ? s.crewItemFull : s.crewItem}>
                              <Text style={s.crewName}>{val(member.name)}</Text>
                              <Text style={s.crewPhone}>{val(member.phone)}</Text>
                            </View>
                          );
                        })}
                      </View>
                    </View>
                  </View>
                )}
                {/* Passengers column — flex-2 with crew+notes for 25/50/25,
                    flex-3 without crew for 75/25, flex-1 in original layout. */}
                {leg.passengers.length > 0 && (
                  <View style={[
                    s.passCol,
                    leg.notes && leg.crew.length > 0 ? { flex: 2 } : null,
                    leg.notes && leg.crew.length === 0 ? { flex: 3 } : null,
                  ]}>
                    <Text style={s.sectionLabel}>Passengers</Text>
                    <View style={[s.whiteCard, { flexGrow: 1 }]}>
                      <View style={s.passGrid}>
                        {leg.passengers.map((passenger, i) => (
                          <View key={i} style={s.passItem}>
                            <Text style={s.passName}>{val(typeof passenger === "object" ? passenger.fullName || "" : passenger)}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                )}
                {/* Empty spacer — preserves the original "only crew" /
                    "only pass" behavior (populated card stays at 50%
                    of the row) when no notes are present. */}
                {!leg.notes && (
                  (leg.crew.length > 0 && leg.passengers.length === 0) ||
                  (leg.crew.length === 0 && leg.passengers.length > 0)
                ) && (
                  <View style={s.passCol} />
                )}
                {/* Note column — always flex:1 (pinned at 25%) when any
                    other card is in the row. When it's alone, it takes
                    the full row via the natural flex-1 with no siblings. */}
                {leg.notes && (
                  <View style={s.noteCol}>
                    <Text style={s.sectionLabel}>Note</Text>
                    <View style={[s.whiteCard, { flexGrow: 1 }]}>
                      <View style={s.noteItem}>
                        <Text style={s.noteText}>{val(leg.notes)}</Text>
                      </View>
                    </View>
                  </View>
                )}
              </View>
            )}

            {/* Additional Services - only show if has data */}
            {leg.additionalServices.length > 0 && (
              <View style={{ marginTop: 12 }}>
                <Text style={s.sectionLabel}>Additional Services</Text>
                <View style={s.serviceTable}>
                  <View style={s.serviceHeaderRow}>
                    <Text style={[s.serviceHeaderCell, { flex: 1 }]}>Name</Text>
                    <Text style={[s.serviceHeaderCell, { flex: 3 }]}>Service Details</Text>
                    {/* <Text style={[s.serviceHeaderCell, { flex: 1.2 }]}>Address</Text> */}
                    {/* <Text style={[s.serviceHeaderCell, { flex: 1 }]}>Phone Number</Text> */}
                    {/* <Text style={[s.serviceHeaderCell, { flex: 1.2 }]}>Confirmation Number</Text> */}
                  </View>
                  {leg.additionalServices.map((service, i) => {
                    // Pick the category icon by service.name — same list
                    // used in the drawer preview. Fallback: plane icon.
                    const iconKey = SERVICE_ICON_KEYS[service.name] || "plane";
                    return (
                    <View key={i} style={s.serviceRow}>
                      <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 3 }}>
                        <Icon d={ICON_PATHS[iconKey]} size={8} color={GRAY_900} />
                        <Text style={s.serviceCellBold}>{val(service.name)}</Text>
                      </View>
                      <Text style={[s.serviceCell, { flex: 3 }]}>{val(service.serviceDetails)}</Text>
                      {/* <Text style={[s.serviceCell, { flex: 1.2 }]}>{val(service.address)}</Text> */}
                      {/* <Text style={[s.serviceCell, { flex: 1 }]}>{val(service.phone)}</Text> */}
                      {/* <Text style={[s.serviceCell, { flex: 1.2 }]}>{val(service.confirmationNumber)}</Text> */}
                    </View>
                    );
                  })}
                </View>
              </View>
            )}
          </View>
        ))}

        {/* AIRCRAFT MEDIA ROW — route map + exterior + interior. Kept inside
            the blue container so the title color reads on the tinted
            background, matching the mobile HTML layout. wrap={false} keeps
            the three cards together on the same page. */}
        {data.aircraftMedia && (
          <View style={s.mediaSection} wrap={false}>
            {!!data.aircraftMedia.title && (
              <Text style={s.mediaTitle}>{data.aircraftMedia.title}</Text>
            )}
            {/* Card count is dynamic: [ map | aircraft image[0] | aircraft image[1] ]
                with slots omitted entirely when an image wasn't loadable —
                avoids duplicate/empty cards when the aircraft has no valid
                photos in the DB. */}
            <View style={s.mediaRow}>
              {data.aircraftMedia.mapImage && (
                <View style={s.mediaCard}>
                  <Image src={data.aircraftMedia.mapImage} style={s.mediaCardImg} />
                </View>
              )}
              {(data.aircraftMedia.aircraftImages || []).map((img, i) => (
                <View key={i} style={s.mediaCard}>
                  <Image src={img} style={s.mediaCardImg} />
                </View>
              ))}
            </View>
          </View>
        )}
      </View>

      {/* FOOTER */}
      <Text style={s.footer}>Page 1 of 1</Text>
    </Page>
  </Document>
  );
};

export default TripSheetPdf;